import { motion } from 'framer-motion';
import {
    Archive,
    Briefcase,
    Calendar,
    Check,
    ChevronDown,
    ChevronRight,
    Clock,
    Copy,
    Edit,
    MapPin,
    Plus,
    Search,
    Sliders,
    Users, Globe
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../utils/api';
import toast from 'react-hot-toast';

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      when: 'beforeChildren',
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: 'spring', stiffness: 300, damping: 24 },
  },
};

const JobsList = () => {
  // States for filters and sorting
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: [],
    location: [],
    department: [],
    employmentType: [],
  });
  const [sortBy, setSortBy] = useState('date');

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Update job status (Draft <-> Active)
  const changeStatus = async (jobId, newStatus) => {
    try {
      await api.put(`/jobs/${jobId}`, { status: newStatus });
      setJobs(prev => prev.map(j => (j.id === jobId || j._id === jobId) ? { ...j, status: newStatus } : j));
      toast.success(`Job marked ${newStatus}`);
    } catch (err) {
      console.error('Job status update failed', err);
      toast.error('Failed to update status');
    }
  };

  // Filter options
  const filterOptions = {
    status: ['Active', 'Draft', 'Closed'],
    location: ['New York', 'San Francisco', 'Chicago', 'Miami', 'Austin', 'Remote'],
    department: ['Engineering', 'Design', 'Marketing', 'Sales', 'Product'],
    employmentType: ['Full-time', 'Part-time', 'Contract', 'Internship'],
  };

  // Handle filter changes
  const handleFilterChange = (type, value) => {
    setFilters((prevFilters) => {
      const updatedFilters = { ...prevFilters };

      if (updatedFilters[type].includes(value)) {
        updatedFilters[type] = updatedFilters[type].filter((item) => item !== value);
      } else {
        updatedFilters[type] = [...updatedFilters[type], value];
      }

      return updatedFilters;
    });
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({
      status: [],
      location: [],
      department: [],
      employmentType: [],
    });
    setSearchQuery('');
  };

  // Apply filters and search
  useEffect(() => {
    const fetchJobs = async () => {
      try {
        setLoading(true);

        const params = { search: searchQuery };

        // Forward status filter to backend if provided
        if (filters.status.length === 1) params.status = filters.status[0];

        const res = await api.get('/jobs/employer/my-jobs', {
          params,
        });

        const remoteJobs = (res.data?.data?.jobs || []).map((job) => ({
          ...job,
          applicants: job.applicationStats?.totalApplications || job.applicants || 0,
        }));
        setJobs(remoteJobs);
      } catch (err) {
        console.error('Failed to fetch jobs', err);
        setJobs([]);
      } finally {
        setLoading(false);
      }
    };

    fetchJobs();
  }, [searchQuery, filters.status]);

  // Apply client filters on fetched jobs
  useEffect(() => {
    if (loading) return;

    let filteredJobs = [...jobs];

    // Apply search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filteredJobs = filteredJobs.filter(
        (job) =>
          job.title?.toLowerCase().includes(query) ||
          job.department?.toLowerCase().includes(query) ||
          job.location?.city?.toLowerCase().includes(query)
      );
    }

    // Apply filters
    Object.keys(filters).forEach((filterType) => {
      if (filters[filterType].length > 0) {
        filteredJobs = filteredJobs.filter((job) => {
          if (filterType === 'location') {
            return (
              filters[filterType].includes(job.location.city) ||
              (job.location.isRemote && filters[filterType].includes('Remote'))
            );
          }
          return filters[filterType].includes(job[filterType]);
        });
      }
    });

    // Apply sorting
    switch (sortBy) {
      case 'title':
        filteredJobs.sort((a, b) => a.title.localeCompare(b.title));
        break;
      case 'applicants':
        filteredJobs.sort((a, b) => b.applicants - a.applicants);
        break;
      case 'date':
      default:
        filteredJobs.sort(
          (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
        break;
    }

    setJobs(filteredJobs);
  }, [searchQuery, filters, sortBy, loading]);

  // Get count of active filters
  const getActiveFilterCount = () => {
    return Object.values(filters).reduce((count, filterGroup) => count + filterGroup.length, 0);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Calculate days remaining until deadline
  const getDaysRemaining = (deadlineString) => {
    if (!deadlineString) return null;
    
    const deadline = new Date(deadlineString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const timeDiff = deadline.getTime() - today.getTime();
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
    
    return daysDiff;
  };

  return (
    <motion.div
      className="space-y-6"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Page header */}
      <motion.div 
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
        variants={itemVariants}
      >
        <div>
          <h1 className="page-title">Job Postings</h1>
          <p className="text-gray-600 mt-1">
            Manage your job listings and track applications
          </p>
        </div>
        <Link
          to="/jobs/new"
          className="btn-primary flex items-center justify-center"
        >
          <Plus size={16} className="mr-2" />
          Post New Job
        </Link>
      </motion.div>

      {/* Search and filters */}
      <motion.div variants={itemVariants}>
        <div className="bg-white rounded-xl shadow-soft p-4 border border-gray-100">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
            {/* Search input */}
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search size={18} className="text-gray-400" />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search jobs by title, department or location"
                className="block w-full pl-10 pr-4 py-2.5 text-gray-900 bg-gray-50 border border-gray-200 rounded-lg font-body text-sm focus:outline-none focus:ring-1 focus:ring-primary-600 focus:border-primary-600"
              />
            </div>

            {/* Filter button */}
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="btn-outline flex items-center"
              >
                <Sliders size={16} className="mr-2" />
                Filters
                {getActiveFilterCount() > 0 && (
                  <span className="ml-2 bg-primary-100 text-primary-700 text-xs rounded-full px-2 py-0.5">
                    {getActiveFilterCount()}
                  </span>
                )}
              </button>

              {/* Sort dropdown */}
              <div className="relative">
                <div className="flex items-center space-x-2">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="select-field py-2.5 pl-4 pr-10 appearance-none bg-white"
                  >
                    <option value="date">Sort by: Date</option>
                    <option value="title">Sort by: Title</option>
                    <option value="applicants">Sort by: Applicants</option>
                  </select>
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                    <ChevronDown size={16} className="text-gray-500" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Filters section */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="font-accent font-medium text-sm text-gray-700">Filter by:</span>
                {getActiveFilterCount() > 0 && (
                  <button
                    onClick={clearFilters}
                    className="text-sm text-primary-600 hover:text-primary-700 flex items-center"
                  >
                    Clear all filters
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {/* Status filters */}
                <div>
                  <p className="block mb-2 text-sm font-semibold text-gray-700 font-accent">
                    Status
                  </p>
                  <div className="space-y-2">
                    {filterOptions.status.map((status) => (
                      <label
                        key={status}
                        className="flex items-center space-x-2 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={filters.status.includes(status)}
                          onChange={() => handleFilterChange('status', status)}
                          className="hidden"
                        />
                        <div
                          className={`w-4 h-4 rounded flex items-center justify-center border ${
                            filters.status.includes(status)
                              ? 'bg-primary-600 border-primary-600'
                              : 'border-gray-300'
                          }`}
                        >
                          {filters.status.includes(status) && (
                            <Check size={12} className="text-white" />
                          )}
                        </div>
                        <span className="text-sm text-gray-700">{status}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Location filters */}
                <div>
                  <p className="block mb-2 text-sm font-semibold text-gray-700 font-accent">
                    Location
                  </p>
                  <div className="space-y-2">
                    {filterOptions.location.map((location) => (
                      <label
                        key={location}
                        className="flex items-center space-x-2 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={filters.location.includes(location)}
                          onChange={() => handleFilterChange('location', location)}
                          className="hidden"
                        />
                        <div
                          className={`w-4 h-4 rounded flex items-center justify-center border ${
                            filters.location.includes(location)
                              ? 'bg-primary-600 border-primary-600'
                              : 'border-gray-300'
                          }`}
                        >
                          {filters.location.includes(location) && (
                            <Check size={12} className="text-white" />
                          )}
                        </div>
                        <span className="text-sm text-gray-700">{location}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Department filters */}
                <div>
                  <p className="block mb-2 text-sm font-semibold text-gray-700 font-accent">
                    Department
                  </p>
                  <div className="space-y-2">
                    {filterOptions.department.map((department) => (
                      <label
                        key={department}
                        className="flex items-center space-x-2 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={filters.department.includes(department)}
                          onChange={() => handleFilterChange('department', department)}
                          className="hidden"
                        />
                        <div
                          className={`w-4 h-4 rounded flex items-center justify-center border ${
                            filters.department.includes(department)
                              ? 'bg-primary-600 border-primary-600'
                              : 'border-gray-300'
                          }`}
                        >
                          {filters.department.includes(department) && (
                            <Check size={12} className="text-white" />
                          )}
                        </div>
                        <span className="text-sm text-gray-700">{department}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Employment type filters */}
                <div>
                  <p className="block mb-2 text-sm font-semibold text-gray-700 font-accent">
                    Employment Type
                  </p>
                  <div className="space-y-2">
                    {filterOptions.employmentType.map((type) => (
                      <label
                        key={type}
                        className="flex items-center space-x-2 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={filters.employmentType.includes(type)}
                          onChange={() => handleFilterChange('employmentType', type)}
                          className="hidden"
                        />
                        <div
                          className={`w-4 h-4 rounded flex items-center justify-center border ${
                            filters.employmentType.includes(type)
                              ? 'bg-primary-600 border-primary-600'
                              : 'border-gray-300'
                          }`}
                        >
                          {filters.employmentType.includes(type) && (
                            <Check size={12} className="text-white" />
                          )}
                        </div>
                        <span className="text-sm text-gray-700">{type}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </motion.div>

      {/* Jobs list */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="loading-spinner w-8 h-8 border-4"></div>
        </div>
      ) : jobs.length === 0 ? (
        <motion.div 
          className="text-center py-12"
          variants={itemVariants}
        >
          <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <Briefcase size={24} className="text-gray-400" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 font-heading">No jobs found</h2>
          <p className="mt-1 text-gray-500">
            Try adjusting your search or filters to find what you're looking for.
          </p>
          <button
            onClick={clearFilters}
            className="mt-4 btn-secondary"
          >
            Clear Filters
          </button>
        </motion.div>
      ) : (
        <div className="space-y-4">
          {jobs.map((job) => (
            <motion.div
              key={job.id}
              className="bg-white rounded-xl shadow-soft border border-gray-100 overflow-hidden"
              variants={itemVariants}
              whileHover={{ y: -4, transition: { duration: 0.2 } }}
            >
              <div className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                  <div className="flex-1">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                      <h2 className="text-xl font-bold text-gray-900 font-heading">
                        {job.title}
                      </h2>
                      <div className="flex flex-wrap gap-2">
                        {job.status === 'Active' && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-success-100 text-success-800">
                            <span className="w-1.5 h-1.5 mr-1.5 rounded-full bg-success-500"></span>
                            Active
                          </span>
                        )}
                        {job.status === 'Draft' && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            <span className="w-1.5 h-1.5 mr-1.5 rounded-full bg-gray-500"></span>
                            Draft
                          </span>
                        )}
                        {job.status === 'Closed' && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-secondary-100 text-secondary-800">
                            <span className="w-1.5 h-1.5 mr-1.5 rounded-full bg-secondary-500"></span>
                            Closed
                          </span>
                        )}
                        {job.featured && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-warning-100 text-warning-800">
                            Featured
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="mt-2 text-gray-700 space-y-1">
                      <div className="flex items-center text-sm">
                        <Briefcase size={15} className="mr-2 text-gray-500" />
                        {job.employmentType} • {job.department}
                      </div>
                      <div className="flex items-center text-sm">
                        <MapPin size={15} className="mr-2 text-gray-500" />
                        {job.location.isRemote
                          ? 'Remote'
                          : `${job.location.city}, ${job.location.state}`}
                      </div>
                      <div className="flex items-center text-sm">
                        <Users size={15} className="mr-2 text-gray-500" />
                        {job.applicants} {job.applicants === 1 ? 'Applicant' : 'Applicants'}
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-4 lg:mt-0 flex flex-col items-end">
                    <div className="text-sm text-gray-500 space-y-1">
                      <div className="flex items-center">
                        <Calendar size={14} className="mr-2 text-gray-400" />
                        <span>Posted: {formatDate(job.publishedAt) || 'Draft'}</span>
                      </div>
                      {job.deadline && (
                        <div className="flex items-center">
                          <Clock size={14} className="mr-2 text-gray-400" />
                          <span>
                            Deadline: {formatDate(job.deadline)}
                            {getDaysRemaining(job.deadline) !== null && getDaysRemaining(job.deadline) >= 0 && (
                              <span className="ml-1 text-xs text-primary-600">
                                ({getDaysRemaining(job.deadline)} days left)
                              </span>
                            )}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="mt-5 flex flex-wrap justify-between items-center gap-3 pt-4 border-t border-gray-100">
                  <div className="flex items-center space-x-3">
                    <Link 
                      to={`/jobs/${job.id || job._id}/edit`}
                      className="btn-outline flex items-center py-1.5"
                    >
                      <Edit size={15} className="mr-1" />
                      Edit
                    </Link>
                    <button className="btn-outline flex items-center py-1.5">
                      <Copy size={15} className="mr-1" />
                      Duplicate
                    </button>
                    <button className="btn-outline flex items-center py-1.5">
                      <Archive size={15} className="mr-1" />
                      Archive
                    </button>
                    {job.status === 'Draft' ? (
                      <button onClick={() => changeStatus(job.id || job._id, 'Active')} className="btn-outline flex items-center py-1.5">
                        <Globe size={15} className="mr-1" />
                        Publish
                      </button>
                    ) : (
                      <button onClick={() => changeStatus(job.id || job._id, 'Draft')} className="btn-outline flex items-center py-1.5">
                        <Archive size={15} className="mr-1" />
                        Unpublish
                      </button>
                    )}
                  </div>
                  
                  <Link
                    to={`/applications?job=${job.id || job._id}`}
                    className="flex items-center text-primary-600 hover:text-primary-700 font-medium text-sm"
                  >
                    View Applications <ChevronRight size={16} />
                  </Link>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </motion.div>
  );
};

export default JobsList; 